<template>
  <div id="" class="modern-list-page">
    <CCard class="rounded shadow-sm page-card">
      <CCardBody>
        <div class="page-toolbar d-flex flex-wrap justify-content-start align-items-center">
          <input
            type="search"
            class="form-control filter-search mr-2 mb-2 mb-md-0"
            placeholder="Search program name"
            v-model="searchValue"
          />
          <select
            v-model="boardTypeFilter"
            class="form-control filter-select mr-2 mb-2 mb-md-0"
          >
            <option value="">All board types</option>
            <option value="board">Board</option>
            <option value="non_board">Non-Board</option>
            <option value="__unset__">Not set</option>
          </select>
          <select
            v-if="canAssignSupervisor"
            v-model="supervisorFilter"
            class="form-control filter-select mr-2 mb-2 mb-md-0"
          >
            <option value="">All supervisors</option>
            <option value="__unset__">Unassigned</option>
            <option
              v-for="supervisor in programSupervisors"
              :key="supervisor.id"
              :value="supervisor.id"
            >
              {{ supervisorLabel(supervisor) }}
            </option>
          </select>

          <button
            v-if="hasProgramFilters"
            type="button"
            class="btn btn-sm btn-light filter-clear mr-2 mb-2 mb-md-0"
            @click="clearProgramFilters"
          >
            <i class="bi bi-x-circle mr-1"></i> Clear
          </button>

          <div class="ml-md-auto d-flex align-items-center mb-2 mb-md-0">
            <button
              @click="
                getPrograms(true, {
                  loader: { enabled: false },
                  refreshLoader: { enabled: true },
                })
              "
              class="btn btn-outline-secondary btn-soft-secondary text-dark mr-2"
            >
              Refresh
            </button>
            <div
              v-if="refreshLoader"
              class="spinner-border spinner-border-sm mr-2"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>
            <button
              v-if="canAssignSupervisor"
              class="btn btn-primary"
              @click="openAddModal"
            >
              Add Program
            </button>
          </div>
        </div>
        <div class="table-responsive programs-table-wrap mt-3">
        <table
          id="table-of-programs"
          class="table table-hover table-sm modern-table mb-0"
        >
          <thead>
            <tr>
              <th>#</th>
              <th>DISC. CODE</th>
              <th>PROGRAM NAME</th>
              <th>FORMER NAME</th>
              <th>MAJOR</th>
              <th>DISC. CODE MAJOR</th>
              <th width="30%">REFERENCE</th>
              <th>BOARD TYPE</th>
              <th>SUPERVISOR ASSIGNED</th>
              <th>UPDATED BY</th>
              <th>UPDATED ON</th>
            </tr>
          </thead>
          <tbody>
            <tr
              class="text-center"
              v-if="!filteredPrograms.length && !tableLoader"
            >
              <td colspan="11">No results found</td>
            </tr>
            <tr class="text-center" v-if="tableLoader">
              <td colspan="11">
                <div class="spinner-border spinner-border-sm mr-1"></div>
                Loading
              </td>
            </tr>
            <tr
              v-for="(program, i) in filteredPrograms"
              :key="program.id"
              @click.stop="editingId !== program.id && pushRoute(program)"
            >
              <th>{{ i + 1 }}</th>
              <td>{{ program.discipline_code }}</td>
              <!-- PROGRAM NAME column -->
              <td @click.stop style="min-width: 200px;">
                <template v-if="editingId === program.id">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="editForm.program_name"
                    placeholder="Program Name"
                  />
                </template>
                <template v-else>
                  <span
                    class="editable-cell"
                    @click.stop="startEdit(program)"
                    title="Click to edit"
                  >
                    {{ program.program_name }}
                    <i class="bi bi-pencil-fill edit-icon"></i>
                  </span>
                </template>
              </td>

              <!-- FORMER NAME column -->
              <td @click.stop style="min-width: 160px;">
                <template v-if="editingId === program.id">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="editForm.former_name"
                    placeholder="Former Name"
                  />
                </template>
                <template v-else>
                  <span
                    class="editable-cell"
                    @click.stop="startEdit(program)"
                    title="Click to edit"
                  >
                    {{ program.former_name || '—' }}
                    <i class="bi bi-pencil-fill edit-icon"></i>
                  </span>
                </template>
              </td>

              <!-- MAJOR column -->
              <td @click.stop style="min-width: 160px;">
                <template v-if="editingId === program.id">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="editForm.major"
                    placeholder="Major"
                  />
                </template>
                <template v-else>
                  <span
                    class="editable-cell"
                    @click.stop="startEdit(program)"
                    title="Click to edit"
                  >
                    {{ program.major || '—' }}
                    <i class="bi bi-pencil-fill edit-icon"></i>
                  </span>
                </template>
              </td>

              <!-- DISC. CODE MAJOR column -->
              <td @click.stop style="min-width: 200px;">
                <template v-if="editingId === program.id">
                  <input
                    type="text"
                    class="form-control form-control-sm mb-1"
                    v-model="editForm.major_discipline_code"
                    placeholder="Disc. Code Major"
                  />
                  <div class="d-flex">
                    <button
                      @click.stop="saveEdit(program)"
                      class="btn btn-sm btn-success mr-1"
                      :disabled="savingId === program.id"
                    >
                      <span
                        v-if="savingId === program.id"
                        class="spinner-border spinner-border-sm"
                      ></span>
                      <span v-else>Save</span>
                    </button>
                    <button
                      @click.stop="cancelEdit"
                      class="btn btn-sm btn-secondary"
                      :disabled="savingId === program.id"
                    >
                      Cancel
                    </button>
                  </div>
                </template>
                <template v-else>
                  <span
                    class="editable-cell"
                    @click.stop="startEdit(program)"
                    title="Click to edit"
                  >
                    {{ program.major_discipline_code || '—' }}
                    <i class="bi bi-pencil-fill edit-icon"></i>
                  </span>
                </template>
              </td>

              <td>
                <div v-for="(ref, index) in program.refs" :key="index">
                  <a href="#" @click.stop="openUrl(ref.file)" class="mr-2">
                    <i class="bi bi-file-pdf"></i> {{ ref.code }}</a
                  >
                </div>
              </td>

              <td @click.stop style="min-width: 150px;">
                <template v-if="canAssignSupervisor">
                  <select
                    class="form-control form-control-sm board-type-select"
                    :value="program.board_type || ''"
                    :disabled="assigningBoardTypeId === program.id"
                    @change="assignBoardType(program, $event.target.value)"
                  >
                    <option value="">—</option>
                    <option value="board">Board</option>
                    <option value="non_board">Non-Board</option>
                  </select>
                </template>
                <template v-else>
                  <span class="board-type-label">{{
                    boardTypeLabel(program.board_type)
                  }}</span>
                </template>
              </td>

              <td @click.stop style="min-width: 240px;">
                <template v-if="canAssignSupervisor">
                  <Multiselect
                    :value="getSupervisorSelection(program)"
                    @input="assignSupervisor(program, $event)"
                    :options="programSupervisors"
                    track-by="id"
                    :custom-label="supervisorLabel"
                    placeholder="Select supervisor…"
                    :searchable="true"
                    :allow-empty="true"
                    :disabled="assigningSupervisorId === program.id"
                    :loading="assigningSupervisorId === program.id"
                    open-direction="bottom"
                    class="supervisor-select"
                  />
                </template>
                <template v-else>
                  <span class="supervisor-name">{{
                    supervisorDisplayName(program)
                  }}</span>
                </template>
              </td>

              <td>{{ program.updated_by }}</td>
              <td>
                <span v-if="program.updated_at">{{
                  program.updated_at | standardDate
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </CCardBody>
    </CCard>

    <!-- Add Program Modal -->
    <AddProgramModal ref="addProgramModal" @programAdded="onProgramAdded" />
  </div>
</template>

<script>
import AddProgramModal from "../components/AddProgramModal.vue";
import Multiselect from "vue-multiselect";

export default {
  name: "Programs",
  data() {
    return {
      updateMode: false,
      searchValue: "",
      boardTypeFilter: "",
      supervisorFilter: "",
      tableLoader: false,
      refreshLoader: false,
      programSupervisors: [],
      assigningSupervisorId: null,
      assigningBoardTypeId: null,
      form: new Form({
        id: "",
        program_name: "",
        discipline_code: "",
        major: "",
        level_code: "",
        cmo: "",
        filename: "",
        file_path: "",
        refs: "",
      }),
      areas: [],
      prefix: "https://drive.google.com/file/d/",
      editingId: null,
      savingId: null,
      editForm: {
        program_name: "",
        former_name: "",
        major: "",
        major_discipline_code: "",
      },
    };
  },
  components: {
    AddProgramModal,
    Multiselect,
  },
  methods: {
    boardTypeLabel(value) {
      if (value === "board") return "Board";
      if (value === "non_board") return "Non-Board";
      return "—";
    },
    supervisorLabel(supervisor) {
      if (!supervisor) return "";
      const name =
        (supervisor.user &&
          [supervisor.user.first_name, supervisor.user.last_name]
            .filter(Boolean)
            .join(" ")) ||
        supervisor.name ||
        supervisor.display_name;
      if (supervisor.designation) {
        return `${name} · ${supervisor.designation}`;
      }
      return name;
    },
    supervisorDisplayName(program) {
      if (program.supervisor) {
        return this.supervisorLabel(program.supervisor);
      }
      if (program.supervisor_name) {
        return program.supervisor_name;
      }
      return "—";
    },
    getSupervisorSelection(program) {
      if (program.supervisor && program.supervisor.id) {
        return (
          this.programSupervisors.find((s) => s.id === program.supervisor.id) ||
          program.supervisor
        );
      }
      if (program.supervisor_id) {
        return (
          this.programSupervisors.find((s) => s.id === program.supervisor_id) ||
          null
        );
      }
      return null;
    },
    getProgramSupervisors() {
      axios
        .get("api/program-supervisors", { params: { active_only: true } })
        .then((res) => {
          this.programSupervisors = res.data || [];
        })
        .catch(() => {});
    },
    assignBoardType(program, value) {
      const boardType = value || null;
      if (boardType === program.board_type) {
        return;
      }

      this.assigningBoardTypeId = program.id;

      axios
        .put(`api/program/${program.id}`, {
          board_type: boardType,
        })
        .then((response) => {
          const programs = this.$store.state.programs;
          const index = programs.findIndex((p) => p.id === program.id);
          const updated = (response.data && response.data.program) || {};

          if (index !== -1) {
            this.$set(
              programs[index],
              "board_type",
              updated.board_type !== undefined ? updated.board_type : boardType
            );
          }

          toastr.success("Board type updated.", "Information");
          this.getPrograms(true, {
            loader: { enabled: false },
            refreshLoader: { enabled: false },
          });
        })
        .catch((error) => {
          const message =
            (error.response && error.response.data.message) ||
            "Failed to update board type.";
          toastr.error(message, "Error");
        })
        .finally(() => {
          this.assigningBoardTypeId = null;
        });
    },
    assignSupervisor(program, supervisor) {
      const supervisorId = supervisor ? supervisor.id : null;
      const currentId =
        (program.supervisor && program.supervisor.id) || program.supervisor_id;

      if (supervisorId === currentId) {
        return;
      }

      this.assigningSupervisorId = program.id;

      axios
        .put(`api/program/${program.id}`, {
          supervisor_id: supervisorId,
        })
        .then((response) => {
          const programs = this.$store.state.programs;
          const index = programs.findIndex((p) => p.id === program.id);
          const updated =
            (response.data && response.data.program) || {};

          if (index !== -1) {
            this.$set(
              programs[index],
              "supervisor_id",
              updated.supervisor_id !== undefined
                ? updated.supervisor_id
                : supervisorId
            );
            this.$set(
              programs[index],
              "supervisor",
              updated.supervisor !== undefined ? updated.supervisor : supervisor
            );
            if (updated.supervisor_name) {
              this.$set(programs[index], "supervisor_name", updated.supervisor_name);
            }
          }

          toastr.success("Supervisor assigned.", "Information");
          this.getPrograms(true, {
            loader: { enabled: false },
            refreshLoader: { enabled: false },
          });
        })
        .catch((error) => {
          const message =
            (error.response && error.response.data.message) ||
            "Failed to assign supervisor.";
          toastr.error(message, "Error");
        })
        .finally(() => {
          this.assigningSupervisorId = null;
        });
    },
    openUrl(path) {
      const url = this.prefix + path + "/view";
      window.open(url, "_blank");
    },
    openModal() {
      this.updateMode = false;
      this.form.reset();
      $("#add-program-modal").modal("show");
    },

    openUpdateProgramModal(program) {
      this.updateMode = true;
      $("#add-program-modal").modal("show");
      this.form.fill(program);
    },

    add() {
      let b = document.getElementById("add-program-btn");
      b.innerHTML = "Adding...";
      b.disabled = true;

      axios
        .post("api/programs", this.form)
        .then((res) => {
          toastr.success(res.data.msg, "Information");
          b.innerHTML = "Add";
          b.disabled = false;
          $("#add-program-modal").modal("hide");
          this.getPrograms(true, {
            loader: { enabled: false },
            refreshLoader: { enabled: false },
          });
        })
        .catch(() => {
          toastr.error("Something went wrong.", "Error");
          b.innerHTML = "Add";
          b.disabled = false;
        });
    },

    openAddModal() {
      this.$refs.addProgramModal.open();
    },

    onProgramAdded() {
      // Refresh the programs list to show the new program
      this.getPrograms(true, {
        loader: { enabled: false },
        refreshLoader: { enabled: true },
      });
    },

    isValidated() {},
    getPrograms(hasChanges, options) {
      if (this.$store.state.isProgramsMounted && !hasChanges) {
        return;
        // execute code when the component is already mounted
      }

      this.refreshLoader = options.refreshLoader.enabled;
      this.tableLoader = options.loader.enabled;
      axios
        .get("api/programs")
        .then((response) => {
          this.tableLoader = false;
          this.refreshLoader = false;
          this.$store.state.programs = response.data;
          this.$store.state.isProgramsMounted = true;
        })
        .catch((error) => {
          this.tableLoader = false;
          this.refreshLoader = false;
        });
    },

    pushRoute(program) {
      this.$router.push({
        name: "ProgramDetails",
        params: {
          id: program.id,
          program: program,
        },
      });
    },

    startEdit(program) {
      this.editingId = program.id;
      this.editForm = {
        program_name: program.program_name || "",
        former_name: program.former_name || "",
        major: program.major || "",
        major_discipline_code: program.major_discipline_code || "",
      };
    },

    cancelEdit() {
      this.editingId = null;
      this.editForm = {
        program_name: "",
        former_name: "",
        major: "",
        major_discipline_code: "",
      };
    },

    saveEdit(program) {
      this.savingId = program.id;
      axios
        .put(`api/program/${program.id}`, {
          program_name: this.editForm.program_name,
          former_name: this.editForm.former_name,
          discipline_code: program.discipline_code,
          level_code: program.level_code,
          major: this.editForm.major,
          major_discipline_code: this.editForm.major_discipline_code,
        })
        .then(() => {
          const programs = this.$store.state.programs;
          const index = programs.findIndex((p) => p.id === program.id);
          if (index !== -1) {
            this.$set(programs[index], "program_name", this.editForm.program_name);
            this.$set(programs[index], "former_name", this.editForm.former_name);
            this.$set(programs[index], "major", this.editForm.major);
            this.$set(
              programs[index],
              "major_discipline_code",
              this.editForm.major_discipline_code
            );
          }
          toastr.success("Updated successfully.", "Information");
          this.editingId = null;
        })
        .catch(() => {
          toastr.error("Something went wrong.", "Error");
        })
        .finally(() => {
          this.savingId = null;
        });
    },
    clearProgramFilters() {
      this.searchValue = "";
      this.boardTypeFilter = "";
      this.supervisorFilter = "";
    },
  },
  computed: {
    canAssignSupervisor() {
      const role = this.$store.state.user && this.$store.state.user.role;
      return role === "Administrator" || role === "Supervisor";
    },
    hasProgramFilters() {
      return (
        this.searchValue.trim() !== "" ||
        this.boardTypeFilter !== "" ||
        this.supervisorFilter !== ""
      );
    },
    filteredPrograms() {
      let temp = this.$store.state.programs;

      if (this.searchValue !== "") {
        temp = temp.filter((program) =>
          program.program_name
            .toUpperCase()
            .includes(this.searchValue.toUpperCase())
        );
      }

      if (this.boardTypeFilter === "__unset__") {
        temp = temp.filter((program) => !program.board_type);
      } else if (this.boardTypeFilter !== "") {
        temp = temp.filter(
          (program) => program.board_type === this.boardTypeFilter
        );
      }

      if (this.supervisorFilter === "__unset__") {
        temp = temp.filter(
          (program) =>
            !program.supervisor_id &&
            !(program.supervisor && program.supervisor.id)
        );
      } else if (this.supervisorFilter !== "") {
        temp = temp.filter(
          (program) =>
            program.supervisor_id == this.supervisorFilter ||
            (program.supervisor &&
              program.supervisor.id == this.supervisorFilter)
        );
      }

      return temp;
    },
  },
  mounted() {
    this.getProgramSupervisors();
    this.getPrograms(true, {
      loader: { enabled: true },
      refreshLoader: { enabled: false },
    });
  },
};
</script>

<style scoped>
#table-of-programs tr {
  cursor: pointer !important;
}

.modern-list-page .page-card {
  border: none;
  border-radius: 12px !important;
  overflow: hidden;
}

.page-toolbar {
  gap: 0.5rem;
  align-items: center;
}

.filter-search {
  min-width: 220px;
  max-width: 320px;
}

.filter-select {
  min-width: 170px;
}

.filter-clear {
  border-radius: 999px;
  font-weight: 600;
}

.page-toolbar .form-control {
  border-radius: 10px;
  border-color: rgba(0, 0, 21, 0.1);
  min-height: 40px;
}

.page-toolbar .form-control:focus {
  border-color: rgba(77, 141, 255, 0.55);
  box-shadow: 0 0 0 0.2rem rgba(77, 141, 255, 0.12);
}

.btn-soft-secondary {
  border-color: rgba(0, 0, 21, 0.1);
  border-radius: 10px;
}

.btn-soft-secondary:hover {
  color: #4d8dff !important;
  background: rgba(77, 141, 255, 0.08);
  border-color: rgba(77, 141, 255, 0.28);
}

.modern-table {
  color: #2b3a55;
  min-width: 1100px;
}

.programs-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 21, 0.06);
}

@media (max-width: 767.98px) {
  .page-toolbar {
    width: 100%;
  }

  .filter-search,
  .filter-select {
    width: 100%;
    max-width: none;
    min-width: 0;
  }

  .page-toolbar .ml-md-auto {
    width: 100%;
    justify-content: flex-start;
  }
}

.modern-table thead th {
  border-top: none;
  border-bottom: 2px solid rgba(0, 0, 21, 0.06);
  color: #768192;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.35px;
  text-transform: uppercase;
}

.modern-table tbody td,
.modern-table tbody th {
  vertical-align: middle;
  border-color: rgba(0, 0, 21, 0.05);
}

.modern-table tbody tr:hover {
  background: rgba(77, 141, 255, 0.04);
}

.editable-cell {
  cursor: pointer;
  display: inline-block;
}

.edit-icon {
  font-size: 0.65rem;
  color: #aaa;
  margin-left: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}

.editable-cell:hover .edit-icon {
  opacity: 1;
}

.editable-cell:hover {
  text-decoration: underline dotted;
}

.supervisor-name {
  font-size: 0.88rem;
  color: #2b3a55;
}

.supervisor-select {
  min-width: 210px;
}

.supervisor-select >>> .multiselect__tags {
  min-height: 34px;
  padding-top: 4px;
  border-radius: 8px;
  border-color: rgba(0, 0, 21, 0.12);
}

.supervisor-select >>> .multiselect__single,
.supervisor-select >>> .multiselect__input,
.supervisor-select >>> .multiselect__placeholder {
  font-size: 0.82rem;
}

.supervisor-select >>> .multiselect__option--highlight,
.supervisor-select >>> .multiselect__option--highlight::after {
  background: #4d8dff;
}

.board-type-select {
  min-width: 120px;
  border-radius: 8px;
  border-color: rgba(0, 0, 21, 0.12);
  font-size: 0.82rem;
}

.board-type-label {
  font-size: 0.88rem;
  color: #2b3a55;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
